/**
 * icons.tsx — Champ Copilot glyphs.
 * The brand marks are 2-tone (white + violet) so they live here rather than in
 * the shared currentColor `@components/icons` registry.
 */
import React from 'react'

interface IconProps {
  className?: string
}

/** launcher / avatar mark: white speech bubble with violet voice-wave bars */
export const VoiceWaveIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 96 96" fill="none" aria-hidden="true">
    <rect x="18" y="22" width="60" height="44" rx="16" fill="#fff" />
    <polygon points="30,64 30,80 46,64" fill="#fff" />
    <rect x="29" y="37" width="6" height="14" rx="3" fill="#7c3aed" />
    <rect x="37" y="33" width="6" height="22" rx="3" fill="#7c3aed" />
    <rect x="45" y="29" width="6" height="30" rx="3" fill="#a855f7" />
    <rect x="53" y="33" width="6" height="22" rx="3" fill="#7c3aed" />
    <rect x="61" y="37" width="6" height="14" rx="3" fill="#7c3aed" />
  </svg>
)

/** inline "Powered by Champ AI" sparkle (uses currentColor) */
export const SparkIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path fill="currentColor" d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2z" />
  </svg>
)

/** agent-step robot, tinted to the agent colour (white head, coloured eyes/mouth) */
export const ChampBotIcon: React.FC<IconProps & { color: string }> = ({ className, color }) => (
  <svg className={className} viewBox="0 0 96 96" fill="none" aria-hidden="true">
    <path d="M48 22 V12" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" />
    <path
      d="M48 4 L50 9.2 L55.6 9.5 L51.3 12.9 L52.9 18.4 L48 15.2 L43.1 18.4 L44.7 12.9 L40.4 9.5 L46 9.2 Z"
      fill="#fff"
    />
    <rect x="18" y="25" width="60" height="51" rx="15" fill="#fff" />
    <rect x="11" y="42" width="6" height="17" rx="3" fill="#fff" />
    <rect x="79" y="42" width="6" height="17" rx="3" fill="#fff" />
    <rect x="29" y="41" width="14" height="10" rx="5" fill={color} />
    <rect x="53" y="41" width="14" height="10" rx="5" fill={color} />
    <rect x="33" y="60" width="3" height="6" rx="1.5" fill={color} />
    <rect x="39" y="57" width="3" height="12" rx="1.5" fill={color} />
    <rect x="45" y="55" width="3" height="16" rx="1.5" fill={color} />
    <rect x="51" y="57" width="3" height="12" rx="1.5" fill={color} />
    <rect x="57" y="60" width="3" height="6" rx="1.5" fill={color} />
  </svg>
)

export const UserIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
    <path d="M4 20a8 8 0 0116 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

export const MicIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 15a3 3 0 003-3V6a3 3 0 00-6 0v6a3 3 0 003 3z" stroke="currentColor" strokeWidth="2" />
    <path d="M19 11a7 7 0 01-14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

export const SendIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 12 L4 4 L10 12 L4 20 Z" fill="currentColor" />
  </svg>
)

export const HistoryIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3.2 9.5a9 9 0 1 1-1 5.3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M3 4.5v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 8v4.2l3 1.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export const NewChatIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path
      d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
)

export const MinimizeIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

export const BackIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export const PlusIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

export const TrashIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v12a1 1 0 01-1 1H7a1 1 0 01-1-1V7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/** rating star — solid when `filled`, hairline outline otherwise (uses currentColor) */
export const StarIcon: React.FC<IconProps & { filled?: boolean }> = ({ className, filled = true }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3.2l2.5 5.1 5.6.8-4.05 3.95.96 5.58L12 16.98 6.03 18.6l.96-5.58L2.94 9.1l5.6-.8L12 3.2z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.5}
      strokeLinejoin="round"
      opacity={filled ? 1 : 0.4}
    />
  </svg>
)

/** calendar glyph for the bookings widget tile (uses currentColor) */
export const CalendarIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="16" rx="2.5" stroke="currentColor" strokeWidth="2" />
    <path d="M3.5 9.5h17M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

/** dollar glyph for the payments widget tile (uses currentColor) */
export const DollarIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path
      d="M16 7.5c-1-1.2-2.5-1.8-4-1.8-2.2 0-3.7 1.2-3.7 2.9 0 4 8 2 8 6 0 1.8-1.7 3.1-4.1 3.1-1.8 0-3.4-.7-4.4-2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/** thin verified tick — direct (first-party) reviews, next to the author (uses currentColor) */
export const CheckIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/** small white chat-bubble for history list items (on the purple tile) */
export const ChatBubbleIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M21 11.5a8.5 8.5 0 01-12.3 7.6L3 21l1.9-5.7A8.5 8.5 0 1121 11.5z" fill="#fff" opacity="0.92" />
  </svg>
)

/** thin support "lifebuoy" ring — the quiet CSM-connect offer glyph (uses currentColor) */
export const LifebuoyIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="12" cy="12" r="3.6" stroke="currentColor" strokeWidth="1.7" />
    <path
      d="M14.6 9.4l3.1-3.1M6.3 17.7l3.1-3.1M9.4 9.4L6.3 6.3M14.6 14.6l3.1 3.1"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
)
